// YouTube utility functions for handling video embeds

/**
 * Extract video ID from various YouTube URL formats
 * @param {string} url - YouTube URL
 * @returns {string|null} - Video ID or null if not found
 */
export const extractVideoId = (url) => {
  if (!url) return null;
  
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/,
    /youtube\.com\/v\/([^&\n?#]+)/,
    /youtube\.com\/watch\?.*v=([^&\n?#]+)/
  ];
  
  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match) return match[1];
  }
  
  return null;
};

/**
 * Create YouTube embed URL with proper parameters
 * @param {string} videoId - YouTube video ID
 * @param {object} options - Embed options
 * @returns {string} - Embed URL
 */
export const createEmbedUrl = (videoId, options = {}) => {
  if (!videoId) return null;
  
  const defaultOptions = {
    autoplay: 0,
    mute: 0,
    rel: 0,
    modestbranding: 1,
    showinfo: 0,
    controls: 1,
    fs: 1, // Allow fullscreen
    cc_load_policy: 0, // Don't show captions by default
    iv_load_policy: 3, // Hide annotations
    ...options
  };
  
  const params = new URLSearchParams();
  Object.entries(defaultOptions).forEach(([key, value]) => {
    params.append(key, value);
  });
  
  return `https://www.youtube.com/embed/${videoId}?${params.toString()}`;
};

/**
 * Get YouTube thumbnail URL
 * @param {string} videoId - YouTube video ID
 * @param {string} quality - Thumbnail quality (default, medium, high, standard, maxres)
 * @returns {string} - Thumbnail URL
 */
export const getThumbnailUrl = (videoId, quality = 'maxresdefault') => {
  if (!videoId) return null;
  return `https://img.youtube.com/vi/${videoId}/${quality}.jpg`;
};

/**
 * Check if a video is likely to be embeddable
 * @param {string} videoId - YouTube video ID
 * @returns {Promise<boolean>} - Whether video is embeddable
 */
export const checkVideoEmbeddable = async (videoId) => {
  if (!videoId) return false;
  
  try {
    // Try to load the embed URL to check if it's accessible
    const embedUrl = createEmbedUrl(videoId);
    const response = await fetch(embedUrl, { method: 'HEAD' });
    return response.ok;
  } catch (error) {
    console.warn('Error checking video embeddability:', error);
    return false;
  }
};

/**
 * Create a fallback message for blocked videos
 * @param {string} videoId - YouTube video ID
 * @param {string} studentName - Student name
 * @returns {string} - Fallback message
 */
export const createFallbackMessage = (videoId, studentName) => {
  const watchUrl = `https://www.youtube.com/watch?v=${videoId}`;
  return `Video temporarily unavailable. Please watch ${studentName}'s testimonial on YouTube: ${watchUrl}`;
};

/**
 * Handle YouTube iframe error
 * @param {Event} event - Error event
 * @param {string} videoId - YouTube video ID
 * @param {string} studentName - Student name
 */
export const handleIframeError = (event, videoId, studentName) => {
  console.error('YouTube iframe error:', event);
  
  // Create fallback content
  const iframe = event.target;
  const container = iframe.parentElement;
  
  if (container) {
    container.innerHTML = `
      <div class="flex flex-col items-center justify-center h-full bg-gray-900 text-white p-8 text-center">
        <div class="mb-4">
          <svg class="w-16 h-16 mx-auto text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"></path>
          </svg>
        </div>
        <h3 class="text-lg font-semibold mb-2">Video Unavailable</h3>
        <p class="text-gray-300 mb-4">This video cannot be displayed due to embedding restrictions.</p>
        <a href="https://www.youtube.com/watch?v=${videoId}" 
           target="_blank" 
           rel="noopener noreferrer"
           class="bg-red-600 hover:bg-red-700 text-white px-6 py-2 rounded-lg transition-colors">
          Watch on YouTube
        </a>
      </div>
    `;
  }
};
